import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { SendPostRequest } from "../../../http";
import { FaEye, FaEyeSlash } from "react-icons/fa";

function UpdateAccount({ onTabSelect }) {
  const patientData = useSelector((store) => store.patient.patientLogin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [password, setPassword] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  // Password strength meter
  const getPasswordStrength = (pwd) => {
    if (!pwd) return "";
    if (pwd.length < 6) return "Weak";
    if (pwd.match(/[A-Z]/) && pwd.match(/[0-9]/) && pwd.length >= 8)
      return "Strong";
    return "Medium";
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setPassword((prev) => ({
      ...prev,
      [name]: value,
    }));

    // ✅ Remove immediate mismatch error here
    setError(null);
    setSuccessMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.new_password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (password.new_password !== password.confirm_password) {
      setError("Passwords do not match"); // ✅ Only checked here
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await SendPostRequest("healthcare/update-account", {
        logged_id: patientData?.logged_id,
        form_data: {
          old_password: password.old_password,
          new_password: password.new_password,
        },
      });

      if (response.status === "success") {
        toast.success(response.message);
        setSuccessMsg("Password updated successfully!");
        setPassword({
          old_password: "",
          new_password: "",
          confirm_password: "",
        });
        onTabSelect("WeightManagment");
      } else {
        setError(response.message || "Failed to update password");
        toast.error(response.message);
      }
    } catch (err) {
      setError("Something went wrong. Try again.");
      console.error("Error updating password:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <form className="row" onSubmit={handleSubmit}>
        <div className="col-lg-12">
          <h4 className="cs_fs_20 cs_medium mb-0">Update Password</h4>
          <div className="cs_height_42 cs_height_xl_25"></div>
          <div className="row">
            <div className="col-lg-6 mt-3">
              <label className="cs_input_label cs_heading_color fw-bold">
                Email
              </label>
              <p>{patientData?.email}</p>
              <div className="cs_height_42 cs_height_xl_25"></div>
            </div>

            {/* Current Password */}
            <div className="col-lg-6 mt-3">
              <label className="cs_input_label cs_heading_color">
                Current Password
              </label>
              <div className="position-relative">
                <input
                  required
                  type={showPassword.old ? "text" : "password"}
                  className={`updateprofile_input ${error?.includes("old password") && "border-danger"
                    }`}
                  placeholder="Enter Current Password"
                  name="old_password"
                  value={password.old_password}
                  onChange={handleOnChange}
                />
                <span
                  className="position-absolute end-0 top-50 translate-middle-y me-2 cursor-pointer"
                  onClick={() =>
                    setShowPassword((prev) => ({ ...prev, old: !prev.old }))
                  }
                >
                  {showPassword.old ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className="cs_height_42 cs_height_xl_25"></div>
            </div>

            {/* New Password */}
            <div className="col-lg-6 mt-3">
              <label className="cs_input_label cs_heading_color">
                New Password
              </label>
              <div className="position-relative">
                <input
                  required
                  type={showPassword.new ? "text" : "password"}
                  className="updateprofile_input"
                  placeholder="Enter New Password"
                  name="new_password"
                  value={password.new_password}
                  onChange={handleOnChange}
                  minLength={6}
                />
                <span
                  className="position-absolute end-0 top-50 translate-middle-y me-2 cursor-pointer"
                  onClick={() =>
                    setShowPassword((prev) => ({ ...prev, new: !prev.new }))
                  }
                >
                  {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <small className="text-muted">
                Minimum 6 characters. Use letters, numbers & symbols.
              </small>
              <div className="mt-0">
                {password.new_password && (
                  <span
                    className={`fw-bold ${getPasswordStrength(password.new_password) === "Weak"
                        ? "text-danger"
                        : getPasswordStrength(password.new_password) === "Medium"
                          ? "text-warning"
                          : "text-success"
                      }`}
                  >
                    Strength: {getPasswordStrength(password.new_password)}
                  </span>
                )}
              </div>
              <div className="cs_height_42 cs_height_xl_25"></div>
            </div>

            {/* Confirm Password */}
            <div className="col-lg-6 mt-3">
              <label className="cs_input_label cs_heading_color">
                Confirm New Password
              </label>
              <div className="position-relative">
                <input
                  required
                  type={showPassword.confirm ? "text" : "password"}
                  className={`updateprofile_input ${error && "border-danger"
                    } ${!error && password.confirm_password ? "border-success" : ""
                    }`}
                  placeholder="Confirm New Password"
                  name="confirm_password"
                  value={password.confirm_password}
                  onChange={handleOnChange}
                />
                <span
                  className="position-absolute end-0 top-50 translate-middle-y me-2 cursor-pointer"
                  onClick={() =>
                    setShowPassword((prev) => ({
                      ...prev,
                      confirm: !prev.confirm,
                    }))
                  }
                >
                  {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className="cs_height_42 cs_height_xl_25 text-danger">
                {error}
              </div>
              {successMsg && (
                <div className="text-success mt-1">{successMsg}</div>
              )}
            </div>

            {/* Submit */}
            <div className="col-lg-12 update_profile_btn_wrapper mt-4">
              <div className="cs_height_18"></div>
              <button
                disabled={loading}
                className="update_profile_btn"
                type="submit"
              >
                {loading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default UpdateAccount;
